/**
 * PersonalEmailCard — email personnel de récupération.
 * Le profil appartient à l'agent : l'email pro (fourni par l'agence) sert
 * au quotidien, l'email perso vérifié garantit la récupération du compte
 * en cas de changement d'agence.
 */
import React, { useEffect, useState } from 'react';
import { ShieldCheck, Mail, Loader2, Trash2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

export default function PersonalEmailCard() {
    const [me, setMe] = useState(null);
    const [email, setEmail] = useState('');
    const [busy, setBusy] = useState(false);

    const load = () => api.get('/auth/me').then((r) => setMe(r.data.user || r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (!email.includes('@')) { toast.error('Adresse invalide'); return; }
        setBusy(true);
        try {
            await api.post('/users/me/personal-email', { email: email.trim() });
            toast.success('Email de confirmation envoyé — vérifiez votre boîte personnelle');
            setEmail('');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Envoi impossible');
        } finally { setBusy(false); }
    };

    const remove = async () => {
        if (!window.confirm('Retirer votre email personnel de récupération ?')) return;
        try {
            await api.delete('/users/me/personal-email');
            toast.success('Email personnel retiré');
            load();
        } catch (e) { toast.error('Suppression impossible'); }
    };

    const verified = me?.personal_email_verified && me?.personal_email;
    const pending = !verified && me?.pending_personal_email;

    return (
        <div className="bg-card rounded-2xl border border-border p-5 sm:p-6"
             data-testid="personal-email-card">
            <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="w-4 h-4 text-[#681793]" />
                <h2 className="font-bold text-foreground">Email personnel de récupération</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
                Votre profil Homly vous appartient. Ajoutez une adresse personnelle
                (différente de l'email fourni par votre agence) pour pouvoir
                récupérer votre compte, votre HomlyScore et votre historique si
                vous changez d'agence. Elle permet aussi de vous connecter et de
                réinitialiser votre mot de passe.
            </p>

            {verified ? (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3"
                     data-testid="personal-email-verified">
                    <div className="flex items-center gap-2 min-w-0">
                        <Mail className="w-4 h-4 text-emerald-500 shrink-0" />
                        <span className="text-sm font-medium truncate">{me.personal_email}</span>
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-emerald-500/15 text-emerald-600 shrink-0">
                            Vérifié
                        </span>
                    </div>
                    <button type="button" onClick={remove} aria-label="Retirer"
                            data-testid="personal-email-remove-btn"
                            className="p-1.5 rounded-md hover:bg-red-500/10 text-red-500 shrink-0">
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            ) : (
                <>
                    {pending && (
                        <div className="flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 mb-3"
                             data-testid="personal-email-pending">
                            <Clock className="w-4 h-4 text-amber-500 shrink-0" />
                            <span className="text-sm">
                                <span className="font-medium">{me.pending_personal_email}</span>
                                {' '}— en attente de confirmation (vérifiez votre boîte mail)
                            </span>
                        </div>
                    )}
                    <div className="flex gap-2 flex-col sm:flex-row">
                        <Input type="email" value={email}
                               onChange={(e) => setEmail(e.target.value)}
                               placeholder="prenom.nom@gmail.com"
                               data-testid="personal-email-input" />
                        <Button onClick={submit} disabled={busy}
                                data-testid="personal-email-submit-btn"
                                className="bg-[#681793] hover:bg-[#7d1cb0] text-white shrink-0">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> :
                                (pending ? 'Renvoyer / changer' : 'Ajouter et vérifier')}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
}
